/**
 * Polling Service
 *
 * Manages background polling for message updates and unread counts.
 * Handles polling intervals, retry limits, and conditional polling based on WebSocket state.
 */

import { MessageControllerService } from "@9amhealth/openapi";
import { POLLING } from "../constants/ChatConstants";
import reportErrorSentry from "src/lib/reportErrorSentry";
import type { ChatBlocDependencies } from "../types/ChatBlocDependencies";
import { WS_STATE } from "src/constants/websocket";

export interface PollingCallbacks {
  onCheckUnreadMessages: () => Promise<void>;
  onLoadFirstPageMessages: () => Promise<void>;
  onLoadNewerMessages: () => Promise<void>;
  log: (message: string, details?: unknown) => void;
}

/**
 * PollingService manages background polling:
 * - Polls for unread message counts
 * - Loads new messages when WebSocket is disconnected
 * - Respects polling limits and intervals
 * - Can be started, stopped, and reset
 */
export class PollingService {
  /**
   * Tracks if polling is currently active
   */
  private isPolling = false;

  /**
   * Counter for polling attempts (limited to prevent infinite loops)
   */
  private pollCount: number = POLLING.MAX_POLL_COUNT;

  /**
   * Timeout handle for the polling loop
   */
  private timeoutHandle: number | undefined;

  constructor(
    private deps: ChatBlocDependencies,
    private callbacks: PollingCallbacks
  ) {}

  /**
   * Start the polling loop
   */
  start(): void {
    if (this.isPolling) return;
    this.isPolling = true;
    this.schedulePoll(POLLING.DEFAULT_INTERVAL_MS);
  }

  /**
   * Stop the polling loop
   */
  stop(): void {
    this.isPolling = false;
    this.clearTimeout();
  }

  /**
   * Reset the poll count
   */
  resetPollCount(): void {
    this.pollCount = POLLING.MAX_POLL_COUNT;
  }

  /**
   * Schedule the next poll
   */
  private schedulePoll(timeout: number = POLLING.DEFAULT_INTERVAL_MS): void {
    this.clearTimeout();
    this.pollCount -= 1;

    if (this.pollCount < 0) {
      return;
    }

    this.timeoutHandle = setTimeout(() => {
      void (async () => {
        this.clearTimeout();
        await this.executePoll();
        this.schedulePoll();
      })();
    }, timeout) as unknown as number;
  }

  /**
   * Execute a single poll cycle
   */
  private async executePoll(): Promise<void> {
    const allowPolling = this.deps.user.hasAppAccess();
    const isInApp = this.deps.app.isInApp();
    const hasAccessToken = Boolean(this.deps.auth.getAccessToken());

    if (!allowPolling || !isInApp || !hasAccessToken) {
      return;
    }

    const promises: Promise<void>[] = [this.callbacks.onCheckUnreadMessages()];

    const wsConnected =
      this.deps.websocket.getConnectionState() === WS_STATE.OPEN;

    if (!wsConnected) {
      this.callbacks.log("WS is disconnected, polling messages");

      // Determine if we need to load first page or just newer messages
      // This is determined by checking if there are messages already loaded
      // The callback will handle this logic
      promises.push(this.callbacks.onLoadNewerMessages());
    }

    await Promise.all(promises);
  }

  /**
   * Check for unread messages (delegates to MessageControllerService)
   */
  async checkUnreadMessages(
    currentUnreadCount: number | undefined,
    onUnreadCountChange: (count: number) => void
  ): Promise<void> {
    try {
      const result = await MessageControllerService.getMessagingStatus();
      const unreadMessages = result.data.unreadMessageCount;
      if (unreadMessages !== currentUnreadCount) {
        onUnreadCountChange(unreadMessages);
      }
    } catch (e) {
      reportErrorSentry(e);
    }
  }

  /**
   * Clear the timeout
   */
  private clearTimeout(): void {
    if (this.timeoutHandle !== undefined) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = undefined;
    }
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    this.stop();
  }
}
